// File: src/components/DocumentTemplates/BoardResolution.js
import React, { useState, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import DocumentLayout from "../DocumentLayout";

const BoardResolution = () => {
    const [companyName, setCompanyName] = useState("Company Name Limited");
    const [meetingDate, setMeetingDate] = useState("2023-01-01");
    const [resolutionTitle, setResolutionTitle] = useState("Approval of Annual Budget");
    const [resolutionText, setResolutionText] = useState(
        "RESOLVED that the annual budget for the fiscal year 2023-2024 as presented to the board is hereby approved."
    );
    const [directors, setDirectors] = useState([
        { name: "John Smith", position: "Chairman", signature: "" },
        { name: "Jane Doe", position: "Director", signature: "" },
    ]);
    const [selectedTemplate, setSelectedTemplate] = useState("1");

    const componentRef = useRef();
    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
        documentTitle: `${companyName}-BoardResolution`,
    });

    const addDirector = () =>
        setDirectors([...directors, { name: "", position: "Director", signature: "" }]);

    const updateDirector = (index, field, value) => {
        const newDirectors = [...directors];
        newDirectors[index][field] = value;
        setDirectors(newDirectors);
    };

    const removeDirector = (index) =>
        setDirectors(directors.filter((_, i) => i !== index));

    const renderTemplate = () => {
        switch (selectedTemplate) {
            case "1": // Classic Formal
                return (
                    <div
                        ref={componentRef}
                        className="bg-white p-10 border-2 border-orange-700 max-w-3xl mx-auto"
                    >
                        <h2 className="text-2xl font-bold text-center underline mb-2">
                            BOARD RESOLUTION
                        </h2>
                        <p className="text-center text-gray-700 mb-6">OF</p>
                        <h1 className="text-3xl font-extrabold text-orange-700 text-center mb-8 uppercase">
                            {companyName}
                        </h1>

                        <p className="mb-4">
                            <b>Meeting Date:</b> {new Date(meetingDate).toDateString()}
                        </p>
                        <p className="mb-4">
                            <b>Resolution Title:</b> {resolutionTitle}
                        </p>
                        <p className="mb-8 whitespace-pre-line">{resolutionText}</p>

                        <h3 className="font-semibold mb-4">Signed by:</h3>
                        <div className="space-y-4">
                            {directors.map((d, i) => (
                                <div key={i} className="flex justify-between items-center">
                                    <div>
                                        <p className="font-medium">{d.name}</p>
                                        <p className="text-sm">{d.position}</p>
                                    </div>
                                    <p className="italic">{d.signature || "________________"}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                );

            case "2": // Modern Card
                return (
                    <div
                        ref={componentRef}
                        className="bg-white p-8 shadow-xl rounded-lg border max-w-3xl mx-auto"
                    >
                        <div className="text-center mb-6">
                            <h2 className="text-xl font-bold text-orange-800 tracking-wide">
                                Board Resolution
                            </h2>
                            <p className="text-gray-500 text-sm">Legal Corporate Record</p>
                        </div>

                        <h1 className="text-2xl font-bold text-center mb-6">{companyName}</h1>

                        <p className="mb-2 text-sm">
                            <b>Meeting Date:</b> {meetingDate}
                        </p>
                        <p className="mb-2 text-sm">
                            <b>Resolution Title:</b> {resolutionTitle}
                        </p>
                        <p className="mb-6">{resolutionText}</p>

                        <div className="border-t pt-4">
                            {directors.map((d, i) => (
                                <div key={i} className="flex justify-between mb-4">
                                    <div>
                                        <p className="font-medium">{d.name}</p>
                                        <p className="text-xs text-gray-600">{d.position}</p>
                                    </div>
                                    <p className="text-xs italic">{d.signature || "Signature"}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                );

            case "3": // Decorative Header
                return (
                    <div
                        ref={componentRef}
                        className="bg-white border max-w-3xl mx-auto overflow-hidden"
                    >
                        <div className="bg-gradient-to-r from-orange-600 to-red-500 text-white p-6">
                            <h2 className="text-2xl font-bold text-center">BOARD RESOLUTION</h2>
                        </div>
                        <div className="p-8">
                            <h1 className="text-2xl font-bold text-orange-700 text-center mb-6">
                                {companyName}
                            </h1>

                            <p className="mb-2">
                                <b>Date:</b> {meetingDate}
                            </p>
                            <p className="mb-2">
                                <b>Subject:</b> {resolutionTitle}
                            </p>
                            <p className="mb-6">{resolutionText}</p>

                            <h3 className="font-semibold mb-4">Board Signatures</h3>
                            <div className="grid grid-cols-2 gap-6">
                                {directors.map((d, i) => (
                                    <div key={i}>
                                        <p className="font-medium">{d.name}</p>
                                        <p className="text-sm">{d.position}</p>
                                        <p className="italic">{d.signature || "______________"}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (

        <div className="space-y-6 bg-slate-50 p-6">
            {/* Inputs */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label className="block text-sm font-medium">Company Name</label>
                    <input
                        type="text"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        className="w-full p-2 border rounded"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium">Meeting Date</label>
                    <input
                        type="date"
                        value={meetingDate}
                        onChange={(e) => setMeetingDate(e.target.value)}
                        className="w-full p-2 border rounded"
                    />
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium">Resolution Title</label>
                <input
                    type="text"
                    value={resolutionTitle}
                    onChange={(e) => setResolutionTitle(e.target.value)}
                    className="w-full p-2 border rounded"
                />
            </div>

            <div>
                <label className="block text-sm font-medium">Resolution Text</label>
                <textarea
                    value={resolutionText}
                    onChange={(e) => setResolutionText(e.target.value)}
                    rows="3"
                    className="w-full p-2 border rounded"
                />
            </div>

            {/* Directors */}
            <div>
                <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium">Directors</label>
                    <button
                        onClick={addDirector}
                        className="px-3 py-1 bg-orange-600 text-white text-sm rounded hover:bg-orange-700"
                    >
                        Add Director
                    </button>
                </div>
                <div className="space-y-4">
                    {directors.map((d, i) => (
                        <div key={i} className="p-4 border rounded bg-gray-50">
                            <div className="flex justify-between items-center mb-2">
                                <h4 className="font-medium">Director {i + 1}</h4>
                                <button
                                    onClick={() => removeDirector(i)}
                                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                                >
                                    ×
                                </button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm">Name</label>
                                    <input
                                        type="text"
                                        value={d.name}
                                        onChange={(e) => updateDirector(i, "name", e.target.value)}
                                        className="w-full p-2 border rounded"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm">Position</label>
                                    <select
                                        value={d.position}
                                        onChange={(e) => updateDirector(i, "position", e.target.value)}
                                        className="w-full p-2 border rounded"
                                    >
                                        <option value="Chairman">Chairman</option>
                                        <option value="Director">Director</option>
                                        <option value="Secretary">Secretary</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm">Signature</label>
                                    <input
                                        type="text"
                                        value={d.signature}
                                        onChange={(e) => updateDirector(i, "signature", e.target.value)}
                                        className="w-full p-2 border rounded"
                                        placeholder="Signature"
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Template Select */}
            <div>
                <label className="block text-sm font-medium">Select Template</label>
                <select
                    value={selectedTemplate}
                    onChange={(e) => setSelectedTemplate(e.target.value)}
                    className="w-full p-2 border rounded"
                >
                    <option value="1">Classic Formal</option>
                    <option value="2">Modern Card</option>
                    <option value="3">Decorative Header</option>
                </select>
            </div>
            <DocumentLayout
                title="Board Resolution"
                description="Official board resolution document"
                onSave={() => alert("Board Resolution saved!")}
                onDownload={handlePrint}
                onPrint={handlePrint}
            >
                {/* Preview */}
                <div className="mt-8 border rounded bg-gray-50">{renderTemplate()}</div>
            </DocumentLayout>
        </div>
    );
};

export default BoardResolution;
